import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { loginRequestSchema, type LoginRequest } from '@witty-board/shared'
import type { TFunction } from 'i18next'
import { ApiError } from '../../api/client.js'
import { useLoginMutation } from '../../api/auth.js'

function loginErrorMessage(error: unknown, t: TFunction): string | null {
  if (!error) return null
  if (error instanceof ApiError) {
    if (error.code === 'INVALID_CREDENTIALS') return t('auth.login.invalidCredentials')
    if (error.code === 'RATE_LIMITED') return t('auth.login.rateLimited')
  }
  return t('auth.login.genericError')
}

export function LoginPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const login = useLoginMutation()
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginRequest>({ resolver: zodResolver(loginRequestSchema) })

  const onSubmit = handleSubmit((values) => {
    login.mutate(values, {
      onSuccess: () => {
        void navigate('/', { replace: true })
      },
    })
  })

  const errorMessage = loginErrorMessage(login.error, t)

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-8 bg-bg px-4">
      <img src="/logo.png" alt="Witty Games" className="h-16 w-auto" />

      <form
        onSubmit={(event) => void onSubmit(event)}
        noValidate
        className="flex w-full max-w-sm flex-col gap-4 rounded-panel bg-surface p-6"
      >
        <h1 className="text-title font-semibold text-text">{t('auth.login.title')}</h1>

        <label className="flex flex-col gap-1">
          <span className="text-meta text-text-muted">{t('auth.login.emailLabel')}</span>
          <input
            type="email"
            autoComplete="email"
            aria-invalid={errors.email ? true : undefined}
            className="rounded-card border border-border bg-surface-hi px-3 py-2 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-accent"
            {...register('email')}
          />
          {errors.email && (
            <span className="text-meta text-danger">{t('auth.login.emailInvalid')}</span>
          )}
        </label>

        <label className="flex flex-col gap-1">
          <span className="text-meta text-text-muted">{t('auth.login.passwordLabel')}</span>
          <input
            type="password"
            autoComplete="current-password"
            aria-invalid={errors.password ? true : undefined}
            className="rounded-card border border-border bg-surface-hi px-3 py-2 text-body text-text outline-none focus-visible:ring-2 focus-visible:ring-accent"
            {...register('password')}
          />
          {errors.password && (
            <span className="text-meta text-danger">{t('auth.login.passwordRequired')}</span>
          )}
        </label>

        {errorMessage && (
          <p role="alert" className="text-meta text-danger">
            {errorMessage}
          </p>
        )}

        <button
          type="submit"
          disabled={login.isPending}
          className="mt-2 rounded-card bg-accent px-4 py-2 text-body font-semibold text-text transition-colors duration-100 hover:bg-accent-hi focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60"
        >
          {login.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
        </button>
      </form>
    </main>
  )
}
